import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import type { ImageToVideoModel } from '../../../hooks/useImageToVideoActions';
import { ASPECT_RATIOS, MODE_RULES, QUALITIES } from '../../../constants/options';
import SectionCard from '../../common/sectionCard';
import TimeDurationSlider from '../../common/timeDurationSlider';
import Spinner from '@/components/ui/spinner';

type SettingsSectionProps = {
  model: ImageToVideoModel;
};

const SettingsSection = ({ model }: SettingsSectionProps) => {
  const { t } = useTranslation();
  const { style, runtime, canGenerate, actions } = model;

  const rules = useMemo(() => MODE_RULES[style.mode], [style.mode]);
  const [durationInput, setDurationInput] = useState<string>(String(style.durationSec));
  const [durationError, setDurationError] = useState<string | null>(null);
  useEffect(() => {
    setDurationInput(String(style.durationSec));
    setDurationError(null);
  }, [style.durationSec]);

  return (
    <div className="space-y-3">
      <SectionCard title={t('settings')}>
        <div className="space-y-3">
          {/* Quality */}
          <div className="rounded-md bg-[#E9F0F1] p-2">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-text-primary">{t('format-quality')}</span>
            </div>
            <div className="mt-1">
              {/* Aspect ratio / format */}
              <div className="mb-3 flex flex-nowrap items-center gap-1">
                <button
                  type="button"
                  onClick={() => actions.onCropToAspectRatioChange('source')}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                    style.cropToAspectRatio === 'source'
                      ? 'bg-primary-light text-white'
                      : 'bg-white text-text-primary'
                  }`}
                >
                  {t('source')}
                </button>
                {ASPECT_RATIOS.map((ratio) => {
                  const allowed = rules.allowedRatios.includes(ratio);
                  const isActive = style.cropToAspectRatio === ratio && allowed;

                  return (
                    <button
                      key={ratio}
                      type="button"
                      disabled={!allowed}
                      onClick={() => {
                        if (!allowed) return;
                        actions.onAspectRatioChange(ratio);
                        actions.onCropToAspectRatioChange(ratio);
                      }}
                      className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                        isActive ? 'bg-primary-light text-white' : 'bg-white text-text-primary'
                      } ${!allowed ? 'cursor-not-allowed opacity-40' : ''}`}
                    >
                      <span
                        className={`inline-flex items-center justify-center rounded-[3px] border-2 ${
                          isActive ? 'border-white bg-white/10' : 'border-gray-400 bg-white'
                        } ${
                          ratio === '16:9' ? 'h-3 w-5' : ratio === '9:16' ? 'h-5 w-3' : 'h-5 w-4'
                        }`}
                      />
                      <span>{ratio}</span>
                    </button>
                  );
                })}
              </div>

              {/* Quality */}
              <div className="flex flex-wrap gap-1.5">
                {QUALITIES.map((quality) => {
                  const isActive = style.quality === quality;
                  return (
                    <button
                      key={quality}
                      type="button"
                      onClick={() => actions.onQualityChange(quality)}
                      className={`min-w-[64px] rounded-full px-4 py-1 text-[10px] font-semibold transition-colors ${
                        isActive ? 'bg-primary-light text-white' : 'bg-white text-text-primary'
                      }`}
                    >
                      {quality}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="rounded-md bg-[#E9F0F1] px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-text-primary">
                {t('image-to-video-duration-per-image')}
              </span>
            </div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-medium text-text-primary">{t('time')}</div>

              <div className="flex items-center">
                <Input
                  type="number"
                  min={rules.minDuration}
                  max={rules.maxDuration}
                  step={1}
                  value={durationInput}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '') {
                      setDurationInput(raw);
                      setDurationError(null);
                      return;
                    }

                    // Keep only the integer part before any decimal separator and strip non-digits.
                    const [integerPart] = raw.split('.');
                    const digitsOnly = integerPart.replace(/\D+/g, '');

                    // If there are no digits at all, ignore the change to preserve the last value.
                    if (digitsOnly === '') {
                      return;
                    }

                    // If the numeric part didn't actually change, ignore (e.g. typing "." after "10").
                    if (digitsOnly === durationInput) {
                      return;
                    }

                    setDurationInput(digitsOnly);

                    const num = Number(digitsOnly);
                    if (Number.isNaN(num)) {
                      return;
                    }

                    if (num < rules.minDuration || num > rules.maxDuration) {
                      setDurationError(
                        t('image-to-video-duration-range-error', {
                          min: rules.minDuration,
                          max: rules.maxDuration,
                        })
                      );
                      return;
                    }

                    setDurationError(null);
                    actions.onDurationChange(num);
                  }}
                  onBlur={() => {
                    if (durationInput === '' || durationError) {
                      setDurationInput(String(style.durationSec));
                      setDurationError(null);
                    }
                  }}
                  className="h-7 w-14 rounded-full border border-black bg-white px-2 py-0 text-center text-[11px] font-semibold text-black"
                />
                <span className="ml-1 text-[11px] font-semibold text-black">s</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <TimeDurationSlider
                value={style.durationSec}
                min={rules.minDuration}
                max={rules.maxDuration}
                step={1}
                onChange={(v) => actions.onDurationChange(v)}
              />
            </div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-text-primary">
                {t('image-to-video-total-time')}:{' '}
              </span>

              <span className="flex items-center gap-1 text-xs font-semibold text-primary-light">
                {t('seconds-short', { value: style.durationSec * style.images.length || 0 })}
              </span>
            </div>
            {durationError ? (
              <div className="mt-1 text-xs text-red-500">{durationError}</div>
            ) : null}
          </div>
        </div>

        <div className="pt-3">
          <Button
            className="w-full"
            size="lg"
            onClick={actions.onGenerateClick}
            disabled={!canGenerate || runtime.status === 'generating'}
          >
            {runtime.status === 'generating' ? (
              <div className="flex items-center justify-center gap-1.5">
                <Spinner /> {t('image-to-video-generating')}
              </div>
            ) : (
              t('create')
            )}
          </Button>
        </div>
      </SectionCard>
    </div>
  );
};

export default SettingsSection;
